import type { ReactElement, ReactNode } from 'react';

/**
 * One `<h1>` per screen, which is what a screen reader's heading jump needs.
 *
 * Eleven screens render this, so it is where the redesign gets most of its
 * leverage. It becomes a **band** rather than floating text: a bottom hairline
 * plus the space under it is what separates "what screen am I on" from the data
 * below, and it does that without adding a second bright panel above the one
 * that holds the information. Under the brightness rule the data card is the
 * loud object on the page; a header card would compete with it.
 *
 * Both new props are optional, so every existing call site still compiles.
 */
export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
}: {
  title: string;
  /**
   * A short uppercase label above the title — usually the section the screen
   * sits in. It is a plain string rather than a lookup into `nav.ts` on
   * purpose: two screens that render this header (SimDetailPage, JobDetailPage)
   * have no nav entry at all, so a coupling to the nav table would have to be
   * optional in exactly the places it was supposed to help.
   */
  eyebrow?: string;
  description?: string | undefined;
  actions?: ReactNode;
}): ReactElement {
  return (
    <header className="mb-[var(--space-6)] flex flex-wrap items-start justify-between gap-[var(--space-4)] border-b border-line pb-[var(--space-5)]">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-[var(--space-1)] text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
            {eyebrow}
          </p>
        ) : null}
        {/* Negative tracking only at this size. At 24px the browser default is
            the single loudest "untouched Tailwind" tell in the whole console. */}
        <h1 className="text-[length:var(--text-2xl)] font-semibold tracking-[var(--tracking-tight)] text-body">
          {title}
        </h1>
        {description ? (
          <p className="mt-[var(--space-1)] max-w-prose text-[length:var(--text-sm)] text-muted">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-[var(--space-2)]">{actions}</div>
      ) : null}
    </header>
  );
}
